import { useLayoutEffect, useRef, useState } from 'react';

// What the time and length pickers' column panels have in common.

// Where the panel goes while it's open. It lives in a portal, so no card or modal can clip it. It
// sits under the field, or over it when there is no room below.
export const usePanelPosition = (open, fieldRef, width, height) => {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = fieldRef.current?.getBoundingClientRect();
      if (!rect) return;
      const below = window.innerHeight - rect.bottom >= height + 12 || rect.top < height + 12;
      setPos({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
        top: below ? rect.bottom + 6 : undefined,
        bottom: below ? undefined : window.innerHeight - rect.top + 6,
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, fieldRef, width, height]);
  return pos;
};

// The option marked data-showing is always in view in its column: in the middle when the panel
// opens, and after that moved only as far as it needs, so a column doesn't jump under the pointer.
export const useRevealShowing = (panelRef, shown, deps) => {
  const revealed = useRef(false);
  useLayoutEffect(() => {
    if (!shown) {
      revealed.current = false;
      return;
    }
    const center = !revealed.current;
    revealed.current = true;
    panelRef.current?.querySelectorAll('.ts-clock-panel__col').forEach((col) => {
      const option = col.querySelector('[data-showing="true"]');
      if (!option) return;
      const box = col.getBoundingClientRect();
      const optionBox = option.getBoundingClientRect();
      const top = optionBox.top - box.top + col.scrollTop;
      const bottom = top + optionBox.height;
      if (center) col.scrollTop = top - (col.clientHeight - optionBox.height) / 2;
      else if (top < col.scrollTop) col.scrollTop = top;
      else if (bottom > col.scrollTop + col.clientHeight) col.scrollTop = bottom - col.clientHeight;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, ...deps]);
};
